import React, { ReactNode, useEffect, useMemo, useState, useRef } from 'react';
import { useIntl } from 'umi';
import { Form, Select, ErrorTip, LoadingTip, SelectOptionWithGroup, Bubble } from 'tea-component';
import { useRequest } from 'ahooks';
import { getNewDataTable } from '@dbl/services/dataSource';
import DatalabGrid from '@/pages/datalab/components/DatalabGrid';
import type { TFilterType, IChartInfo } from '../types';
import useVirtualReferenceElement from '@/pages/datalab/utils/hooks/useVirtualReferenceElement';
import { AI_ANALYSIS_TYPE } from '@dbl/core/dashboard';
import { LANGUAGE_EN } from '@home/_config/config';
import { accentInsensitiveMatch } from '@/pages/datalab/utils/accentMap';

interface IChartDatasetItem {
  table_id?: string;
  customize_name?: string;
  customize_name_en?: string;
  table_name?: string;
  driver?: string;
  db_name?: string;
  extra1?: string;
  scheme?: string;
  table_desc?: string;
  [key: string]: any;
}

interface IDataTableItem {
  table_id: string;
  customize_name?: string;
  customize_name_en?: string;
  table_name?: string;
  driver?: string;
  db_name?: string;
  extra1?: string;
  scheme?: string;
  table_desc?: string;
}

type TDatasetOption = SelectOptionWithGroup & {
  rawText?: string;
  tableName?: string;
  customizeName?: string;
  customizeNameEn?: string;
  driver?: string;
  dbName?: string;
  extra1?: string;
  scheme?: string;
  tableDesc?: string;
};
type THoverInfo = string | null;

interface IFilterDatasetProps {
  status?: 'error' | undefined;
  dataset: string;
  onChange: (value: string, context?: any) => void;
  gameCode: string;
  viewList: IChartInfo[];
  allFilterDatasetId: string[];
  filterType: TFilterType;
}

interface IDatasetOptionDetailProps {
  option?: TDatasetOption;
  isEn: boolean;
  formatMessage: (descriptor: { id: string; }) => string;
}

const EMPTY_TEXT = '-';

const DatasetOptionDetail: React.FC<IDatasetOptionDetailProps> = ({ option, isEn, formatMessage }) => {
  if (!option) return null;

  const {
    customizeName,
    customizeNameEn,
    tableName,
    driver,
    dbName,
    scheme,
    tableDesc,
  } = option;
  const displayName = (isEn ? customizeNameEn : customizeName) || tableName || EMPTY_TEXT;
  const databaseName = `${dbName}${ (typeof scheme === 'string') ? `(${scheme})` : '' }`;
  const detailRows = [
    { label: formatMessage({ id: 'dbl_create_dashboard_dataset_detail_name' }), value: displayName },
    { label: formatMessage({ id: 'dbl_create_dashboard_dataset_detail_database_type' }), value: driver || EMPTY_TEXT },
    { label: formatMessage({ id: 'dbl_create_dashboard_dataset_detail_database_name' }), value: databaseName },
    { label: formatMessage({ id: 'dbl_create_dashboard_dataset_detail_schema' }), value: scheme || EMPTY_TEXT },
    { label: formatMessage({ id: 'dbl_create_dashboard_dataset_detail_description' }), value: tableDesc || EMPTY_TEXT },
  ];

  return (
    <div className="dbl-exit-filter-dataset-detail">
      <DatalabGrid
        colCount={2}
        gap="8px 20px"
        columnWidthMode={['max-content', '1fr']}
        verticalAlign="start"
      >
        {detailRows.map((row) => (
          <React.Fragment key={row.label}>
            <DatalabGrid.Item className="dbl-exit-filter-dataset-detail-label">
              {row.label}
            </DatalabGrid.Item>
            <DatalabGrid.Item className="dbl-exit-filter-dataset-detail-value">
              {row.value}
            </DatalabGrid.Item>
          </React.Fragment>
        ))}
      </DatalabGrid>
    </div>
  );
};

const FilterDataset: React.FC<IFilterDatasetProps> = (props) => {
  const { status, dataset, onChange, gameCode, viewList, allFilterDatasetId } = props;
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const datasetGroup = {
    'related': formatMessage({id: 'dbl_create_dashboard_related_dataset'}),
    'other': formatMessage({id: 'dbl_create_dashboard_other_dataset'}),
  };
  // 数据表请求有防抖时间, 导致下拉选选项在显示的时候会 一下显示选项一下显示loading, 闪来闪去;
  // 所以单独设置一个loading状态, 用来显示加载态
  const [loading, setLoading] = useState<boolean>(false);
  
  // 数据集option
  const [datasetOptions, setDatasetOptions] = useState<TDatasetOption[]>([]);
  const [optionMap, setOptionMap] = useState<Map<string, TDatasetOption>>(new Map());
  
  // Hover 相关状态
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const { referenceElement, setAnchor } = useVirtualReferenceElement({
    refreshAfterAnchorInRaf: false,
    scrollContainerRef: scrollContainerRef as React.RefObject<HTMLElement>,
  });
  const [hoverInfo, setHoverInfo] = useState<THoverInfo>(null);
  const {tableIds, relatedOptions} = useMemo(() => {
    const optionMap = new Map();
    // 图表列表遍历
    viewList?.forEach((view: IChartInfo) => {
      if (view.chartType === 'text' || view.chartType === AI_ANALYSIS_TYPE) {
        return;
      }
      // 图表中的数据表遍历
      view?.chart_dataset_list?.forEach((table: IChartDatasetItem) => {
        
        const option = optionMap.get(table?.table_id);
        // 如果 已经创建数据表对应的选项的chartId中 不包含当前图表的taskId, 那么需要拼接
        if (option) {
          if (!option?.chartId?.includes?.(view.task_id)) {
            // 且选项中的chartId
            option.chartId += `,${view.task_id}`;
          }
          
        } else {
          // 新建数据表选项
          optionMap.set(table?.table_id, {
            groupKey: 'related',
            chartId: view.task_id,
            text: table?.customize_name || table?.customize_name_en || table?.table_name,
            rawText: table?.customize_name || table?.customize_name_en || table?.table_name,
            tableName: table?.table_name,
            customizeName: table?.customize_name,
            customizeNameEn: table?.customize_name_en,
            value: table?.table_id,
          });
        }
      });
    });
    
    return {tableIds: Array.from(optionMap.keys()), relatedOptions: [...optionMap.values()]};
  }, []);
  
  const { error: datasetError, run } = useRequest<{ data_list?: IDataTableItem[] }, [string?]>(async (searchWord?: string) => {
    // 上传参数
    const newParams = {
      game_code: gameCode,
      keywords: searchWord,
      order_by: 'use_count',
      desc: true,
      table_ids: allFilterDatasetId ? allFilterDatasetId : [],
      page: 1,
      page_size: searchWord ? 200 : 50,
      from: 'dashboard_filter'
    };
    
    const res = await getNewDataTable(newParams);
    return res as { data_list?: IDataTableItem[] };
  }, {
    debounceWait: 300,
    onSuccess: (data: { data_list?: IDataTableItem[] }) => {
      const nextOptionMap = new Map<string, TDatasetOption>();
      const nextDatasetOptions: TDatasetOption[] = [];
      const dataList = data?.data_list || [];
      const tableDetailMap = new Map<string, IDataTableItem>();
      dataList.forEach((item) => {
        if (!item?.table_id) return;
        tableDetailMap.set(item.table_id, item);
      });

      const enrichByTableDetail = (opt: TDatasetOption): TDatasetOption => {
        const detail = tableDetailMap.get(opt.value);
        if (!detail) return opt;

        return {
          ...opt,
          driver: detail.driver ?? opt.driver,
          dbName: detail.db_name ?? opt.dbName,
          scheme: detail.scheme ?? opt.scheme,
          tableDesc: detail.table_desc ?? opt.tableDesc,
        };
      };

      const pushOption = (opt: TDatasetOption): void => {
        const enrichedOpt = enrichByTableDetail(opt);
        nextOptionMap.set(enrichedOpt.value, enrichedOpt);
        nextDatasetOptions.push({
          ...enrichedOpt,
          text: (
            <div
              data-option-value={enrichedOpt.value}
              className="dbl-exit-filter-dataset-option"
            >
              {enrichedOpt.text}
            </div>
          ),
        });
      };
      relatedOptions.forEach(pushOption);
      dataList.forEach((item) => {
        if (tableIds.includes(item?.table_id)) return; // 将与图表相关的数据表过滤出去
        const option: TDatasetOption = {
          value: item?.table_id, // 为了和 related类型的value统一格式, 在Column中解析时统一
          text: item?.customize_name || item?.customize_name_en || item?.table_name, // 数据表名称不区分中英文
          rawText: item?.customize_name || item?.customize_name_en || item?.table_name,
          groupKey: 'other',
          tableName: item?.table_name,
          customizeName: item?.customize_name,
          customizeNameEn: item?.customize_name_en,
          driver: item?.driver,
          dbName: item?.db_name,
          scheme: item?.scheme,
          tableDesc: item?.table_desc,
        };
        pushOption(option);
      });

      setDatasetOptions(nextDatasetOptions);
      setOptionMap(nextOptionMap);
      setLoading(false);
    }
  });
  
  const datasetTips: ReactNode = useMemo(() => {
    if (loading) return (<LoadingTip />);
    if (datasetError) return (<ErrorTip />);
    // if (datasetOptions.length === 0) return (<EmptyTip/>);
    return null;
    
  }, [loading, datasetError]);

  useEffect(()=> {
    if (!optionMap.size) return;
    if (dataset && !optionMap.has(dataset)) {
      onChange('');
    }
  },[dataset, optionMap]);
  // 事件委托：挂在 document 上，避免依赖下拉容器的挂载时机。
  // 仅在挂载时注册一次，避免 hover 状态变化导致重复绑定/解绑全局监听。
  useEffect(() => {
    const handleMouseOver = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      const optionEl = target.closest('[data-option-value]') as HTMLElement | null;
      if (!optionEl) return;
      const container = optionEl.closest('.dbl-exit-filter-dataset-overlay') as HTMLElement | null;
      if (!container) return;

      const value = optionEl.getAttribute('data-option-value');
      if (!value) return;

      setHoverInfo((prevHoverInfo) => {
        if (prevHoverInfo === value) {
          return prevHoverInfo;
        }

        // 记录下拉容器，供虚拟 reference 在容器滚动时刷新定位。
        (scrollContainerRef as React.MutableRefObject<HTMLElement | null>).current = container;
        setAnchor(optionEl);
        return value;
      });
    };

    const handleMouseOut = (e: MouseEvent): void => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const container = target.closest('.dbl-exit-filter-dataset-overlay');
      if (!container) return;

      const relatedTarget = e.relatedTarget;
      if (
        relatedTarget instanceof Element &&
        relatedTarget.closest('.dbl-exit-filter-dataset-overlay')
      ) {
        return;
      }

      setHoverInfo(null);
      setAnchor(null);
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      (scrollContainerRef as React.MutableRefObject<HTMLElement | null>).current = null;
    };
  }, [setAnchor]);
  
  return (
    <>
      <Form.Item label={formatMessage({id: 'dbl_create_dashboard_dataset'})} showStatusIcon={false} status={status} message={status === 'error' ? formatMessage({id: 'dbl_common_cannot_be_empty'}) : ''}>
        <Select
          overlayClassName={'dbl-exit-filter-dataset-overlay'}
          matchButtonWidth
          appearance="button"
          groups={datasetGroup}
          options={(loading || datasetError) ? [] : datasetOptions}
          value={dataset}
          onChange={onChange}
          placeholder={formatMessage({id: 'dbl_create_dashboard_dataset_placeholder'})}
          tips={datasetTips}
          searchable
          searchPlaceholder={formatMessage({id: 'dbl_create_dashboard_dataset_search_placeholder'})}
          onSearchValueChange={(value) => {
            setLoading(true);
            run(value?.trim());
          }}
          filter={(inputValue, option: any) => {
            if (option.groupKey === 'other') return true;
            return accentInsensitiveMatch(
              inputValue,
              String(option.rawText ?? ''),
              option.tableName,
            );
          }}
          autoClearSearchValue={false}
          listHeight={340}
        />
      </Form.Item>
      <Bubble
        referenceElement={referenceElement}
        className="dbl-exit-filter-dataset-bubble"
        dark
        placement="left"
        visible={Boolean(hoverInfo)}
        content={(
          <DatasetOptionDetail
            option={hoverInfo ? optionMap.get(hoverInfo) : undefined}
            isEn={Boolean(isEn)}
            formatMessage={formatMessage}
          />
        )}
      />
    </>
  );
};

export default FilterDataset;